import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "components/button";
import Icon from "components/icon";
import DateRangePicker from "components/date-range-picker";
import { Columns, TableSortState } from "components/table";
import ActionMenu from "components/shared/action-menu";
import { toast } from "react-toastify";
import { useAppDispatch } from "store";
import { setGlobalComponent } from "store/global-components";
import useHistoryPanel from "hooks/shared/useHistoryPanel";
import { useAppTranslation } from "locale/useAppTranslation";
import { editVehicleTypePageRouteName } from "../edit/edit.page";
import {
  useVehicleTypeServiceV1ListVehicleTypesQuery,
  useVehicleTypeServiceV1ActivateVehicleTypesMutation,
  useVehicleTypeServiceV1DeactivateVehicleTypesMutation,
} from "_services/modules/vehicle-type";
import { errorHandler } from "_services/errorHandler";

export interface VehicleTypeRow {
  id: string;
  vehicleType: string;
  vehicleCategory: string;
  tempOption: string | null;
  isChassis: boolean;
  isContainer: boolean;
  status: "active" | "inactive";
  createdBy: string;
  createdAt: string;
  updatedBy: string;
  updatedAt: string;
}

const useVehicleTypeList = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useAppTranslation("vehicleType");
  const [selectedKeys, setSelectedKeys] = useState<Array<string | number>>([]);
  const [sortState, setSortState] = useState<TableSortState>(null);
  const [filterValues, setFilterValues] = useState<Record<string, string>>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [bulkAction, setBulkAction] = useState<string>("");
  const [showBulkConfirmModal, setShowBulkConfirmModal] = useState(false);
  const [showToggleConfirmModal, setShowToggleConfirmModal] = useState(false);
  const [toggleRow, setToggleRow] = useState<VehicleTypeRow | null>(null);
  const historyPanel = useHistoryPanel("vehicle_type");

  const [activateVehicleTypes] = useVehicleTypeServiceV1ActivateVehicleTypesMutation();
  const [deactivateVehicleTypes] = useVehicleTypeServiceV1DeactivateVehicleTypesMutation();

  const { data: apiResponse, isLoading, refetch } = useVehicleTypeServiceV1ListVehicleTypesQuery({
    page: currentPage,
    size: rowsPerPage,
    sortBy: sortState?.columnId ?? undefined,
    sortOrder: sortState?.direction ?? undefined,
    status: filterValues.status || undefined,
    vehicleTypeName: filterValues.vehicleType || undefined,
    vehicleCategoryName: filterValues.vehicleCategory || undefined,
    tempTypeName: filterValues.tempOption || undefined,
    createdAtFrom: filterValues.createdAt?.split("|")[0] || undefined,
    createdAtTo: filterValues.createdAt?.split("|")[1] || undefined,
    updatedAtFrom: filterValues.updatedAt?.split("|")[0] || undefined,
    updatedAtTo: filterValues.updatedAt?.split("|")[1] || undefined,
  });

  const tableData: VehicleTypeRow[] = useMemo(() => {
    if (!apiResponse?.vehicle_types) return [];
    return apiResponse.vehicle_types.map((item) => ({
      id: item.id ?? "",
      vehicleType: item.vehicle_type ?? "",
      vehicleCategory: item.category_name ?? "",
      tempOption: item.temp_type_name || null,
      isChassis: item.is_chassis ?? false,
      isContainer: item.is_container ?? false,
      status: (item.status as "active" | "inactive") ?? "inactive",
      createdBy: item.created_by ?? "",
      createdAt: item.created_at ?? "",
      updatedBy: item.updated_by ?? "",
      updatedAt: item.updated_at ?? "",
    }));
  }, [apiResponse]);

  const totalPages = apiResponse?.metadata?.total_page ?? 1;

  useEffect(() => {
    dispatch(
      setGlobalComponent({ title: t("page-title-list"), hasBackButton: false })
    );
  }, [dispatch, t]);

  const selectedRows = tableData.filter((row) => selectedKeys.includes(row.id));
  const hasMixedStatus =
    selectedRows.length > 0 &&
    new Set(selectedRows.map((r) => r.status)).size > 1;

  const isBulkActionDisabled =
    selectedKeys.length === 0 || hasMixedStatus;

  const handleApplyBulkAction = () => {
    if (!bulkAction || isBulkActionDisabled) return;
    setShowBulkConfirmModal(true);
  };

  const handleConfirmBulkAction = async () => {
    try {
      const ids = selectedKeys.map(String);
      if (bulkAction === "activate") {
        await activateVehicleTypes({ vehicleTypeV1VehicleTypeBulkStatusChangeReq: { ids } }).unwrap();
        toast.success(t("toast-activate-success"));
      } else {
        await deactivateVehicleTypes({ vehicleTypeV1VehicleTypeBulkStatusChangeReq: { ids } }).unwrap();
        toast.success(t("toast-deactivate-success"));
      }
      setShowBulkConfirmModal(false);
      setBulkAction("");
      setSelectedKeys([]);
    } catch (error) {
      console.error("Bulk action failed:", error);
      errorHandler(error);
    }
  };

  const handleToggleStatus = (row: VehicleTypeRow) => {
    setToggleRow(row);
    setShowToggleConfirmModal(true);
  };

  const handleConfirmToggleStatus = async () => {
    try {
      if (!toggleRow) return;
      if (toggleRow.status === "active") {
        await deactivateVehicleTypes({ vehicleTypeV1VehicleTypeBulkStatusChangeReq: { ids: [toggleRow.id] } }).unwrap();
        toast.success(t("toast-deactivate-success"));
      } else {
        await activateVehicleTypes({ vehicleTypeV1VehicleTypeBulkStatusChangeReq: { ids: [toggleRow.id] } }).unwrap();
        toast.success(t("toast-activate-success"));
      }
      setShowToggleConfirmModal(false);
      setToggleRow(null);
      refetch();
    } catch (error) {
      console.error("Toggle status failed:", error);
      errorHandler(error);
    }
  };

  const handleFilterChange = (values: Record<string, string>) => {
    setFilterValues(values);
    setCurrentPage(1);
  };

  const handleEdit = (row: VehicleTypeRow) => {
    navigate(editVehicleTypePageRouteName(row.id.toString()));
  };

  const columns: Columns<VehicleTypeRow>[] = useMemo(
    () => [
      {
        id: "no",
        fieldId: "index",
        label: t("col-no"),
        width: "60px",
      },
      {
        id: "vehicleType",
        fieldId: "vehicleType",
        label: t("label-vehicle-type"),
        sortable: true,
        filterable: true,
        filterPlaceholder: t("col-search-placeholder"),
      },
      {
        id: "tempOption",
        fieldId: "tempOption",
        label: t("label-temp-option"),
        sortable: true,
        filterable: true,
        filterPlaceholder: t("col-search-placeholder"),
        render: (row) => (
          <span>{row.tempOption ?? "-"}</span>
        ),
      },
      {
        id: "vehicleCategory",
        fieldId: "vehicleCategory",
        label: t("label-vehicle-category"),
        sortable: true,
        filterable: true,
        filterPlaceholder: t("col-search-placeholder"),
      },
      {
        id: "status",
        fieldId: "status",
        label: t("label-status"),
        sortable: true,
        filterable: true,
        filterOptions: [
          { label: t("col-all-data"), value: "" },
          { label: t("status-active"), value: "active" },
          { label: t("status-inactive"), value: "inactive" },
        ],
        render: (row) => (
          <div className="inline-flex items-center gap-2 text-sm" data-testid={`col-status-${row.id}`}>
            <span
              className={`h-3 w-3 rounded-full ${
                row.status === "active"
                  ? "bg-primary-600 border-2 border-primary-100"
                  : "bg-greyScale-60 border-2 border-greyScale-90"
              }`}
            />
            <span className="capitalize">{row.status}</span>
          </div>
        ),
      },
      {
        id: "createdAt",
        fieldId: "createdBy",
        fieldId2: "createdAt",
        label: t("col-created-by"),
        sortable: true,
        filterable: true,
        renderFilter: ({ value, onChange }) => {
          const [from = "", to = ""] = value.split("|");
          return (
            <DateRangePicker
              fromValue={from}
              toValue={to}
              onChange={(f, t) => onChange(`${f}|${t}`)}
              reserveHelperSpace={false}
            />
          );
        },
      },
      {
        id: "updatedAt",
        fieldId: "updatedBy",
        fieldId2: "updatedAt",
        label: t("col-updated-by"),
        sortable: true,
        filterable: true,
        renderFilter: ({ value, onChange }) => {
          const [from = "", to = ""] = value.split("|");
          return (
            <DateRangePicker
              fromValue={from}
              toValue={to}
              onChange={(f, t) => onChange(`${f}|${t}`)}
              reserveHelperSpace={false}
            />
          );
        },
      },
      {
        id: "action",
        fieldId: "id",
        label: t("col-action"),
        width: "140px",
        dragDisabled: true,
        render: (row) => (
          <div
            className="flex items-center gap-2"
            onClick={(e) => e.stopPropagation()}
          >
            <Button
              type="button"
              variant="outline"
              color="primary"
              size="small"
              iconLeft={<Icon name="edit-2" size={14} />}
              onClick={() => handleEdit(row)}
              data-testid="btn-edit-type"
            >
              {t("btn-edit")}
            </Button>
            <ActionMenu
              items={[
                {
                  label: row.status === "active" ? t("btn-deactivate") : t("btn-activate"),
                  onClick: () => handleToggleStatus(row),
                  variant: row.status === "active" ? "danger" : "default",
                  testId: row.status === "active" ? "btn-deactivate-type-list" : "btn-activate-type-list",
                },
                {
                  label: t("btn-track-history"),
                  onClick: () => historyPanel.openPanel(row.id, row.vehicleType),
                  testId: "btn-track-history-type-list",
                },
              ]}
            />
          </div>
        ),
      },
    ],
    [t],
  );

  return {
    data: tableData,
    isLoading,
    columns,
    sortState,
    setSortState,
    filterValues,
    setFilterValues,
    handleFilterChange,
    selectedKeys,
    setSelectedKeys,
    currentPage,
    setCurrentPage,
    rowsPerPage,
    setRowsPerPage,
    totalPages,
    navigate,
    bulkAction,
    setBulkAction,
    isBulkActionDisabled,
    showBulkConfirmModal,
    setShowBulkConfirmModal,
    handleApplyBulkAction,
    handleConfirmBulkAction,
    showToggleConfirmModal,
    setShowToggleConfirmModal,
    toggleRow,
    handleConfirmToggleStatus,
    historyPanel,
  };
};

export default useVehicleTypeList;
